import { Activity, Calendar, Clock, Moon, TrendingUp, Zap } from 'lucide-react'

export interface AutomationTemplate {
  id: string
  title: string
  description: string
  icon: typeof Activity
  color: string
  gradient: string
  periodicity: 'after_workout' | 'daily' | 'weekly' | 'monthly'
  prompt: string
  scheduleHour: number
  scheduleDayOfWeek?: number
  scheduleDayOfMonth?: number
  emailResults: boolean
  emailMode: 'always' | 'tool'
  category: 'training' | 'recovery' | 'performance'
}

export const automationColors = [
  { name: 'Blue', color: '#60a5fa', gradient: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)' },
  { name: 'Green', color: '#34d399', gradient: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)' },
  { name: 'Purple', color: '#a78bfa', gradient: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)' },
  { name: 'Orange', color: '#f59e0b', gradient: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' },
  { name: 'Pink', color: '#f472b6', gradient: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)' },
  { name: 'Red', color: '#ef4444', gradient: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)' },
  { name: 'Cyan', color: '#22d3ee', gradient: 'linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)' },
]

export const automationTemplates: AutomationTemplate[] = [
  {
    id: 'weekly-training-summary',
    title: 'Weekly Training Summary',
    description: "Get a comprehensive summary of the week's training every Monday morning",
    icon: Calendar,
    color: '#60a5fa',
    gradient: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
    periodicity: 'weekly',
    prompt: `Analyze this week's training data and provide a comprehensive summary including:
- Total training volume (hours and distance)
- Training intensity distribution
- Key achievements and notable workouts
- Recovery patterns
- Recommendations for the upcoming week

Keep the summary concise but insightful.`,
    scheduleHour: 9,
    scheduleDayOfWeek: 0, // Monday
    emailResults: true,
    emailMode: 'always',
    category: 'training',
  },
  {
    id: 'post-workout-analysis',
    title: 'Post-Workout Analysis',
    description: 'Automatically analyze and provide insights after each workout upload',
    icon: Activity,
    color: '#34d399',
    gradient: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
    periodicity: 'after_workout',
    prompt: `Analyze this workout and provide:
- Performance highlights and key metrics
- Comparison to recent similar workouts
- Training zones and intensity analysis
- Recovery recommendations based on workout intensity
- Any areas of concern or improvement opportunities

Be specific and actionable in your feedback.`,
    scheduleHour: 9,
    emailResults: true,
    emailMode: 'tool',
    category: 'training',
  },
  {
    id: 'recovery-check',
    title: 'Recovery Status Check',
    description: 'Weekly assessment of recovery metrics and readiness',
    icon: Moon,
    color: '#a78bfa',
    gradient: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)',
    periodicity: 'weekly',
    prompt: `Review recovery data from the past week and provide:
- Overall recovery score and trends
- Sleep quality analysis
- Training load vs recovery balance
- Heart rate variability trends (if available)
- Recommendations for the upcoming training week
- Any red flags that need attention

Focus on actionable insights for optimal recovery.`,
    scheduleHour: 20,
    scheduleDayOfWeek: 6, // Sunday evening
    emailResults: true,
    emailMode: 'always',
    category: 'recovery',
  },
  {
    id: 'monthly-progress-report',
    title: 'Monthly Progress Report',
    description: 'Comprehensive monthly performance and progress analysis',
    icon: TrendingUp,
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    periodicity: 'monthly',
    prompt: `Create a comprehensive monthly progress report including:
- Month-over-month performance trends
- Goal progress and achievements
- Training consistency and volume analysis
- Performance improvements in key metrics
- Areas of strength and areas needing attention
- Recommendations for next month's training focus

Make it motivating while being realistic about progress.`,
    scheduleHour: 9,
    scheduleDayOfMonth: 1, // First day of month
    emailResults: true,
    emailMode: 'always',
    category: 'performance',
  },
  {
    id: 'daily-readiness-check',
    title: 'Daily Readiness Check',
    description: "Morning assessment of training readiness and today's focus",
    icon: Zap,
    color: '#f472b6',
    gradient: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',
    periodicity: 'daily',
    prompt: `Provide a quick daily readiness assessment:
- Review yesterday's training and recovery data
- Assess current readiness for today's training
- Recommend today's training intensity based on recovery status
- Highlight any concerns or adjustments needed
- Motivational message for the day

Keep it brief and actionable - this should be a quick morning read.`,
    scheduleHour: 7,
    emailResults: true,
    emailMode: 'always',
    category: 'training',
  },
  {
    id: 'race-preparation-tracker',
    title: 'Race Preparation Tracker',
    description: 'Weekly check-in on race preparation progress',
    icon: Clock,
    color: '#ef4444',
    gradient: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)',
    periodicity: 'weekly',
    prompt: `Track race preparation progress:
- Days until race
- Training phase assessment (build/peak/taper)
- Key workouts completed this week
- Preparation checklist status
- Adjustments needed for race readiness
- Mental preparation and confidence check

Provide constructive feedback to build confidence while ensuring proper preparation.`,
    scheduleHour: 19,
    scheduleDayOfWeek: 4, // Friday
    emailResults: true,
    emailMode: 'always',
    category: 'performance',
  },
]

export const getTemplatesByCategory = (category: 'training' | 'recovery' | 'performance') => {
  return automationTemplates.filter((t) => t.category === category)
}

export const getTemplateById = (id: string) => {
  return automationTemplates.find((t) => t.id === id)
}
